import { ArrowCounterClockwise, FunnelSimple } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import {
  BooleanParam,
  createEnumParam,
  JsonParam,
  NumberParam,
  type QueryParamConfigMap,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import { LeadPreview } from "common/components/leads";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import Pagination from "common/components/ui/Pagination";
import { DEFAULT_LEADS_SEARCH_PARAMS } from "common/constants";
import useLeadsInfo from "common/datahooks/useLeadsInfo";
import {
  getPaginationInfoText,
  typedObjectEntries,
} from "common/helpers/utils";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { NavTabsContentContainer } from "common/layouts/WorkspaceLayout";
import type {
  FacetParam,
  LeadsSearchParams,
  SelectedFacetParam,
} from "common/types";

import { LEADS_TAB_ITEMS } from "../../constants";
import useLeads from "../../datahooks/useLeads";
import ExportLeads from "./ExportLeads";
import Heading from "./Heading";
import LeadsEmptyPlaceholder from "./LeadsEmptyPlaceholder";
import LeadsList from "./LeadsList";
import LeadsTable from "./LeadsTable";
import MobileFilters from "./MobileFilters";
import SearchFilters from "./SearchFilters";

import type { JSX } from "react";

import NoResultsImage from "assets/images/empty-placeholders/result-not-found.png";
import SyncingLeadsImage from "assets/images/empty-placeholders/syncing-leads.png";

const paramsConfig = typedObjectEntries(DEFAULT_LEADS_SEARCH_PARAMS).reduce(
  (accumulator, [key, value]) => {
    if (typeof value === "boolean") {
      accumulator[key] = withDefault(BooleanParam, value);
    } else if (typeof value === "string") {
      accumulator[key] = withDefault(StringParam, value);
    } else {
      accumulator[key] = withDefault(JsonParam, value);
    }
    return accumulator;
  },
  {} as QueryParamConfigMap,
);

const pageSize = 8;
export default function AllLeads() {
  const isDesktop = useTwBreakpoint("lg");
  const [searchParams, setSearchParams] = useQueryParams(paramsConfig, {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  }) as [
    LeadsSearchParams<FacetParam>,
    (searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>) => void,
  ];
  const { hasFilters, resetAllParams, resetAllFilters } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );
  const [keywordInputValue, setKeywordInputValue] = useState(
    searchParams.keywords,
  );
  const [previewLeadId, setPreviewLeadId] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0), {
    removeDefaultsFromUrl: true,
    updateType: "replaceIn",
  });
  const [orderSort, setOrderSort] = useQueryParam<"asc" | "desc">(
    "order",
    withDefault(createEnumParam(["asc", "desc"]), "asc"),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );
  const { leadsInfo, isFetchingLeadsInfo, leadsInfoError, refetchLeadsInfo } =
    useLeadsInfo(searchParams);
  const { leads, isFetchingLeads, leadsError, refetchLeads } = useLeads(
    searchParams,
    {
      page,
      order: orderSort,
      pageSize,
    },
  );

  useEffect(() => {
    setPage(0);
    setIsPreviewOpen(false);
  }, [searchParams]);

  useEffect(() => {
    setIsPreviewOpen(false);
  }, [page]);

  const isEmpty =
    !isFetchingLeads &&
    (!leads || !leads.length) &&
    !hasFilters &&
    !searchParams.keywords;

  let mainContent: JSX.Element;

  if (leadsError || leadsInfoError) {
    mainContent = (
      <ErrorPlaceholder
        errorMessage="Could not display leads data"
        onRetry={() => {
          if (leadsInfoError) {
            refetchLeadsInfo();
          }
          if (leadsError) {
            refetchLeads();
          }
        }}
      />
    );
  } else {
    if (
      leadsInfo &&
      Object.values(leadsInfo.accountsSync).every((isSyncing) => !!isSyncing)
    ) {
      mainContent = (
        <EmptyPlaceholder
          imageSrc={SyncingLeadsImage}
          title="Your Leads are loading"
          subtitle="Leads are currently synchronizing with your LinkedIn account. This process can take up to 24 hours, depending on your number of connections"
        />
      );
    } else if (isEmpty) {
      mainContent = <LeadsEmptyPlaceholder />;
    } else {
      const filtersComponent = (
        <SearchFilters
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        />
      );

      const MainComponent = isDesktop ? LeadsTable : LeadsList;

      mainContent = (
        <>
          <div className="flex h-fit shrink-0 flex-col lg:mr-4 lg:w-[280px] xl:w-[326px]">
            {isDesktop ? (
              <div className="rounded-4xl border border-black-200">
                <div className="flex items-center gap-x-2.5 border-b border-b-black-200 p-3">
                  <div className="flex size-10 items-center justify-center rounded-2xl bg-black-100">
                    <FunnelSimple size={20} />
                  </div>

                  <span className="text-md-bold">Filters</span>

                  {hasFilters && (
                    <Button
                      variant="secondary-black"
                      className="ml-auto"
                      size="sm"
                      onClick={resetAllParams}
                      leftIcon={<ArrowCounterClockwise />}
                    >
                      Reset all
                    </Button>
                  )}
                </div>

                <div className="py-2">{filtersComponent}</div>
              </div>
            ) : (
              <MobileFilters
                filtersComponent={filtersComponent}
                resetFilters={hasFilters && resetAllFilters}
              />
            )}
          </div>
          <div className="flex flex-col lg:w-2/3 lg:grow">
            <Heading
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              keywordInputValue={keywordInputValue}
              setKeywordInputValue={setKeywordInputValue}
            />
            {leads && !leads.length ? (
              <EmptyPlaceholder
                title="No results found"
                subtitle="There were no results found for your search. Try searching for something else"
                imageSrc={NoResultsImage}
                actionComponent={
                  <Button
                    leftIcon={<ArrowCounterClockwise />}
                    variant="secondary-black"
                    onClick={() => {
                      setKeywordInputValue("");
                      resetAllParams();
                    }}
                  >
                    Clear search
                  </Button>
                }
              />
            ) : (
              <MainComponent
                leads={leads}
                isLoading={isFetchingLeads}
                openPreview={({ id }) => {
                  if (isPreviewOpen && id === previewLeadId) {
                    setIsPreviewOpen(false);
                  } else {
                    setPreviewLeadId(id);
                    setIsPreviewOpen(true);
                  }
                }}
                orderSort={orderSort}
                setOrderSort={setOrderSort}
              />
            )}
            {!isFetchingLeadsInfo && !leadsInfoError && (
              <div className="flex flex-col-reverse items-center justify-between gap-4 lg:flex-row">
                <span className="text-xs text-black-600">
                  {getPaginationInfoText(page, pageSize, leadsInfo.totalLeads)}
                </span>
                <Pagination
                  pageSize={pageSize}
                  itemCount={leadsInfo.totalLeads}
                  setPage={setPage}
                  selectedIndex={page}
                  isLastPageHidden
                  size="md"
                />
              </div>
            )}
          </div>
        </>
      );
    }
  }

  return (
    <div className="flex gap-x-2 grow h-full">
      <NavTabsContentContainer
        primaryActionsComponent={
          <RenderIf condition={!isEmpty}>
            <ExportLeads
              searchParams={searchParams}
              totalLeadsCount={leadsInfo?.totalLeads ?? 0}
              isFetching={isFetchingLeadsInfo}
            />
          </RenderIf>
        }
        tabItems={LEADS_TAB_ITEMS}
      >
        <div className="flex flex-col gap-y-4 lg:flex-row grow">
          {mainContent}
        </div>
      </NavTabsContentContainer>

      <LeadPreview
        leadId={previewLeadId}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </div>
  );
}
